import { Injectable } from '@angular/core';
import { environment } from '@eview/core/environments/environment';
import { FormAttribute, FormAttributeType } from '../form/form-attribute';
import { formatVulnerabilityRowsDisplay } from '../form/vulnerability-rows';
import * as moment from 'moment';

export interface ExportColumnOption {
  key: string;
  label: string;
  group: 'meta' | 'form';
}

export interface ExportDownloadFormats {
  excel: boolean;
  pdf: boolean;
}

export interface ExportDownloadOptions {
  columnKeys: string[];
  formats: ExportDownloadFormats;
  /** Download basename without extension (e.g. "Reporte de Conflictividad Electoral"). */
  documentName?: string;
}

export interface ExportColumnDef {
  key: string;
  label: string;
  group?: string;
  parent_key?: string;
}

export interface ExportPostsPayload {
  success: boolean;
  columns?: ExportColumnDef[];
  headers?: string[];
  data?: Record<string, unknown>[];
}

/** Stable meta columns offered in the export picker (aligned with API). */
export const EXPORT_META_COLUMNS: ExportColumnOption[] = [
  { key: 'id', label: 'ID del informe', group: 'meta' },
  { key: 'title', label: 'Título del informe', group: 'meta' },
  { key: 'content', label: 'Descripción', group: 'meta' },
  { key: 'status', label: 'Estado del informe', group: 'meta' },
  { key: 'status_detail_derivado', label: 'Detalle (derivado)', group: 'meta' },
  { key: 'status_detail_cerrado', label: 'Detalle (cerrado)', group: 'meta' },
  { key: 'conflict_status', label: 'Estado del conflicto', group: 'meta' },
  { key: 'priority', label: 'Criticidad', group: 'meta' },
  { key: 'published', label: 'Publicado', group: 'meta' },
  { key: 'unpublish_reason', label: 'Motivo de no publicación', group: 'meta' },
  { key: 'analysis', label: 'Análisis', group: 'meta' },
  { key: 'updates_count', label: 'Nº de actualizaciones', group: 'meta' },
  { key: 'updates', label: 'Actualizaciones', group: 'meta' },
  { key: 'created', label: 'Creado', group: 'meta' },
  { key: 'updated', label: 'Actualizado', group: 'meta' },
  { key: 'post_date', label: 'Fecha de reporte del informe', group: 'meta' },
  // Peru posts: mgmt_lev_3=Departamento, mgmt_lev_1=Provincia, mgmt_lev_2=Distrito
  { key: 'mgmt_lev_3', label: 'Departamento', group: 'meta' },
  { key: 'mgmt_lev_1', label: 'Provincia', group: 'meta' },
  { key: 'mgmt_lev_2', label: 'Distrito', group: 'meta' },
  { key: 'latitude', label: 'Latitud', group: 'meta' },
  { key: 'longitude', label: 'Longitud', group: 'meta' },
  { key: 'author_realname', label: 'Autor del informe', group: 'meta' },
  { key: 'author_contact', label: 'Contacto del autor', group: 'meta' },
  { key: 'author_email', label: 'Correo del autor', group: 'meta' },
  { key: 'source', label: 'Fuente', group: 'meta' },
  { key: 'proceso', label: 'Proceso electoral', group: 'meta' },
  { key: 'institucion', label: 'Institución', group: 'meta' },
  { key: 'linked_posts', label: 'Informes vinculados', group: 'meta' },
  { key: 'parent_id', label: 'ID padre', group: 'meta' }
];

@Injectable({ providedIn: 'root' })
export class ExportDownloadService {
  private pending: ExportDownloadOptions | null = null;

  setPendingOptions(options: ExportDownloadOptions): void {
    this.pending = {
      columnKeys: [...(options.columnKeys || [])],
      formats: {
        excel: !!options.formats?.excel,
        pdf: !!options.formats?.pdf
      },
      documentName: (options.documentName || '').trim() || undefined
    };
  }

  consumePendingOptions(): ExportDownloadOptions | null {
    const next = this.pending;
    this.pending = null;
    return next;
  }

  /**
   * Filename for a reports export: "Reporte de {formato}".
   * Falls back to "Reporte de informes" when the form name is missing.
   */
  buildReportDocumentName(formName?: string | null): string {
    const name = String(formName || '')
      .trim()
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return name ? `Reporte de ${name}` : 'Reporte de informes';
  }

  buildColumnOptions(
    formAttributes: FormAttribute[] | null | undefined,
    formId?: number
  ): ExportColumnOption[] {
    // formId kept for callers; published is now always available.
    void formId;
    const meta = [...EXPORT_META_COLUMNS];

    const formCols = (formAttributes || [])
      .filter(fa => this.isExportableFormAttribute(fa))
      .sort((a, b) => (a.priority || 0) - (b.priority || 0))
      .map(fa => ({
        key: fa.key,
        label: fa.label || fa.key,
        group: 'form' as const
      }));

    return [...meta, ...formCols];
  }

  private isExportableFormAttribute(fa: FormAttribute): boolean {
    if (!fa?.key || fa.response_private) {
      return false;
    }
    if (
      fa.type === FormAttributeType.Title ||
      fa.type === FormAttributeType.Description ||
      fa.type === FormAttributeType.Point ||
      fa.type === FormAttributeType.Media
    ) {
      return false;
    }
    return true;
  }

  /**
   * Build AOA sheet from API payload + optional column filter.
   */
  buildSheetRows(
    payload: ExportPostsPayload,
    columnKeys?: string[] | null
  ): unknown[][] {
    const columns = this.resolveColumns(payload);
    const selected = this.filterColumns(columns, columnKeys);
    if (!selected.length) {
      return [];
    }
    const header = selected.map(c => c.label);
    const body = (payload.data || []).map(row =>
      selected.map(c => this.cellDisplayValue(row?.[c.key]))
    );
    return [header, ...body];
  }

  /**
   * Parent rows keys (e.g. vulnerabilidad_filas) also match expanded
   * children (vulnerabilidad_filas__1__population).
   */
  private filterColumns(
    columns: ExportColumnDef[],
    columnKeys?: string[] | null
  ): ExportColumnDef[] {
    if (!columnKeys?.length) {
      return columns;
    }
    const wanted = new Set(columnKeys);
    return columns.filter(c => {
      if (wanted.has(c.key)) {
        return true;
      }
      if (c.parent_key && wanted.has(c.parent_key)) {
        return true;
      }
      // Expanded key: parent__n__field
      for (const k of columnKeys) {
        if (c.key.startsWith(`${k}__`)) {
          return true;
        }
      }
      return false;
    });
  }

  /**
   * Prefer human-readable text; never dump raw JSON objects/arrays to the sheet.
   */
  private cellDisplayValue(v: unknown): string | number | boolean {
    if (v == null) {
      return '';
    }
    if (typeof v === 'number' || typeof v === 'boolean') {
      return v;
    }
    if (typeof v === 'string') {
      const asDate = this.tryFormatDateTime(v);
      if (asDate !== null) {
        return asDate;
      }
      const formatted = this.tryFormatRowsJson(v);
      return formatted !== null ? formatted : v;
    }
    if (typeof v === 'object') {
      const formatted = this.tryFormatRowsJson(v);
      if (formatted !== null) {
        return formatted;
      }
      if (Array.isArray(v)) {
        const parts = v
          .map(item => {
            if (item == null) {
              return '';
            }
            if (typeof item === 'string' || typeof item === 'number') {
              const asDate = this.tryFormatDateTime(String(item));
              if (asDate !== null) {
                return asDate;
              }
              const inner = this.tryFormatRowsJson(String(item));
              return inner !== null ? inner : String(item);
            }
            if (typeof item === 'object') {
              return this.tryFormatRowsJson(item) ?? '';
            }
            return String(item);
          })
          .filter(Boolean);
        return parts.join(' | ');
      }
      return '';
    }
    return String(v);
  }

  /**
   * Turn ISO / SQL datetimes into the app display format.
   * @returns formatted string, or null if not a datetime
   */
  private tryFormatDateTime(raw: string): string | null {
    const t = String(raw || '').trim();
    if (!t) {
      return null;
    }
    // YYYY-MM-DD or ISO/W3C/SQL datetime (avoid treating plain text as dates)
    if (
      !/^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/.test(
        t
      )
    ) {
      return null;
    }
    const m = moment.utc(t);
    if (!m.isValid()) {
      return null;
    }
    // Pure date (YYYY-MM-DD) → date only; otherwise always keep date + time
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
      return m.format(environment.format.date);
    }
    return m.format(environment.format.dateTime);
  }

  /** @returns formatted text, or null if value is not rows JSON */
  private tryFormatRowsJson(raw: unknown): string | null {
    const text = formatVulnerabilityRowsDisplay(raw);
    if (!text) {
      return null;
    }
    if (Array.isArray(raw)) {
      return text;
    }
    if (raw && typeof raw === 'object') {
      return text;
    }
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (t.startsWith('[') && t.includes('{')) {
        return text;
      }
    }
    return null;
  }

  private resolveColumns(payload: ExportPostsPayload): ExportColumnDef[] {
    if (Array.isArray(payload.columns) && payload.columns.length) {
      return payload.columns.filter(c => c && c.key);
    }
    const headers = payload.headers || [];
    const sample = payload.data?.[0];
    const keys = sample ? Object.keys(sample) : [];
    return headers.map((label, i) => ({
      key: keys[i] || `col_${i}`,
      label: label || `col_${i}`
    }));
  }
}
